import { NavPage } from '@/components/page/Breadcrumbs';
import { PageContainer } from '@/components/page/PageContainer';
import { PageHeader } from '@/components/page/PageHeader';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { VerticalMenu } from '@/components/shared/menu/VerticalMenu';
import { gqlToContract } from '@/services/contract/useContracts';
import { useGetContract } from '@/services/contract/useGetContract';
import { FC } from 'react';
import { FiFileText } from 'react-icons/fi';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import strings from '../../admin.translations';
import { deactivateReactivateTabConfig } from '../../common';
import AdminDeactivateReactivateContractCard from './AdminDeactivateReactivateContractCard';
import AdminEditContractDetailsCard from './AdminEditContractDetailsCard';

export const AdminEditContractPage: FC = () => {
  const { tabName, contractId } = useParams();

  if (!contractId) throw new Error('Missing contract id');

  const { state } = useLocation();

  const navigate = useNavigate();

  const getContract = useGetContract();

  const contract = state?.contract ? gqlToContract(state.contract) : getContract(contractId);

  if (!contract) {
    throw new Error(`Contract with ID ${contractId} not found`);
  }

  const breadcrumbs: NavPage[] = [
    {
      name: strings.page.admin,
      to: '/admin'
    },
    {
      name: strings.contractsSection.edit.contract,
      to: '/admin/contracts'
    },
    {
      name: contract.name,
      to: `/admin/contracts/${contractId}`
    }
  ];

  const { icon: DeactivateReactivateIcon, label: deactivateReactivateLabel } =
    deactivateReactivateTabConfig[contract.active ? 'active' : 'inactive'];

  const sideMenuOptions = [
    {
      icon: <FiFileText className="h-5 w-5" />,
      label: strings.contractsSection.edit.contractDetails,
      anchorId: 'edit-contract',
      element: <AdminEditContractDetailsCard contract={contract} />
    },
    {
      icon: <DeactivateReactivateIcon className="h-5 w-5" />,
      label: deactivateReactivateLabel,
      anchorId: 'deactivate-reactivate',
      element: <AdminDeactivateReactivateContractCard contract={contract} />
    }
  ];

  const allElements = (
    <>
      <AdminEditContractDetailsCard contract={contract} />
      <AdminDeactivateReactivateContractCard contract={contract} />
    </>
  );

  const selectedTab = tabName ?? sideMenuOptions[0].anchorId;
  const handleTabSelect = (anchorId: string) => {
    navigate(`/admin/contracts/${contractId}/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={<PageHeader pageTitle={strings.page.title} icon={<FiFileText />} breadcrumbs={breadcrumbs} />}
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        <div className="hidden lg:flex gap-5 w-full">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            isScroll={false}
            selectedTab={selectedTab}
          />
        </div>
        <div className="lg:hidden">
          <VerticalMenu
            onTabSelect={handleTabSelect}
            sideMenuOptions={sideMenuOptions}
            allElements={allElements}
            isScroll={true}
            selectedTab={selectedTab}
          />
        </div>
      </PageSizer>
    </PageContainer>
  );
};
